import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ApiClient, type Account, type FetchLike } from '@scoreboard/api-client';

// Session animateur (doc 02 §5.2) : l'access token vit EN MÉMOIRE, jamais en
// localStorage ; le refresh est un cookie HttpOnly géré par l'API. Au boot on tente
// un refresh silencieux — c'est ce qui « restaure » la session après un reload.

export const API_BASE_URL: string =
  (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? globalThis.location?.origin ?? '';

export type SessionStatus = 'loading' | 'authenticated' | 'anonymous';

interface SessionContextValue {
  api: ApiClient;
  status: SessionStatus;
  account: Account | null;
  setAccount: (account: Account | null) => void;
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export const browserFetch: FetchLike = async (url, init) => {
  const response = await globalThis.fetch(url, init as RequestInit);
  return { ok: response.ok, status: response.status, text: () => response.text() };
};

export function createApiClient(baseUrl = API_BASE_URL): ApiClient {
  return new ApiClient({ baseUrl, fetch: browserFetch });
}

export function SessionProvider({
  children,
  client,
}: {
  children: React.ReactNode;
  client?: ApiClient;
}): React.ReactElement {
  const apiRef = useRef<ApiClient>(client ?? createApiClient());
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [account, setAccount] = useState<Account | null>(null);

  useEffect(() => {
    let cancelled = false;
    const api = apiRef.current;
    void (async () => {
      const restored = api.token !== null || (await api.refreshSession());
      if (cancelled) {
        return;
      }
      if (!restored) {
        setStatus('anonymous');
        return;
      }
      try {
        const me = await api.me();
        if (!cancelled) {
          setAccount(me);
          setStatus('authenticated');
        }
      } catch {
        if (!cancelled) {
          setStatus('anonymous');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAccount = useCallback((next: Account | null) => {
    setAccount(next);
    setStatus(next === null ? 'anonymous' : 'authenticated');
  }, []);

  const logout = useCallback(async () => {
    await apiRef.current.logout();
    setAccount(null);
    setStatus('anonymous');
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({ api: apiRef.current, status, account, setAccount: handleAccount, logout }),
    [status, account, handleAccount, logout],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (value === null) {
    throw new Error('useSession must be used inside <SessionProvider>');
  }
  return value;
}

export function useApi(): ApiClient {
  return useSession().api;
}
